import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, FormControl, InputLabel, Select, MenuItem,
  CircularProgress, Alert, Grid, Paper, Card
} from '@mui/material';
import SideDrawer from '../Components/newcomponents/SideDrawer';
import { useParams } from 'react-router-dom';
import { db } from '../Components/auth/firebaseConfig';
import { collection, getDocs, query, where } from 'firebase/firestore';
import Navbar from '../Components/LandingComponent/Navbar.js';
import ReactECharts from 'echarts-for-react';
import MilestoneManager from './MilestoneManager';
import BoardDropdown from '../Components/newcomponents/BoardDropdown.js';
import MetricsCharts from '../Components/MetricsCharts';
import CRMMetricsCharts from '../Components/CRMMetricsCharts';
import { fetchProjectDetails, fetchCardsForBoard, fetchUserDetails, fetchBoards, fetchAssignedGroupsByEmail, fetchAnalyticsData, fetchRecentCardsFromDB, fetchTasksByBoard, fetchBoardDetails } from '../DatabaseApis/DBQuery.js'
import { getCookie } from '../utils/cookieUtils';

const DashboardPage = () => {
  const [boards, setBoards] = useState([]);
  const [selectedBoardId, setSelectedBoardId] = useState('ALL_BOARDS');
  const [selectedBoardDetails, setSelectedBoardDetails] = useState(null);
  
  // Helper to get templateType for selected board
  const selectedBoard = boards.find(board => board.id === selectedBoardId);
  
  // Helper function to check if board is CRM based on templateType or columns
  const checkIfCRMTemplate = (board) => {
    if (!board) return false;
    
    // First check templateType field (check both templateType and template for compatibility)
    if (board.templateType === 'CRM' || board.template === 'CRM') return true;
    
    // Fallback: check if columns match CRM columns
    const crmColumns = [
      'Prospecting',
      'Qualified',
      'Proposal Sent',
      'Negotiation',
      'Won',
      'Lost',
      'Withdrawn'
    ];
    
    // Extract column names - handle both string array and object array formats
    let boardCols = [];
    if (Array.isArray(board.columns)) {
      boardCols = board.columns.map(col => {
        if (typeof col === 'string') {
          return col.trim();
        } else if (col && typeof col === 'object' && col.name) {
          return col.name.trim();
        }
        return '';
      }).filter(Boolean);
    }
    
    // Check if columns match CRM columns
    // Must have at least Won or Lost, and most of the CRM-specific columns
    if (boardCols.length >= 5) {
      const hasWonOrLost = boardCols.includes('Won') || boardCols.includes('Lost');
      const matchingColumns = crmColumns.filter(crmCol => 
        boardCols.some(bCol => bCol === crmCol || bCol.toLowerCase() === crmCol.toLowerCase())
      );
      
      // If it has Won/Lost and at least 4 other CRM columns match, consider it CRM
      if (hasWonOrLost && matchingColumns.length >= 4) {
        return true;
      }
    }
    
    return false;
  };
  
  // If 'ALL_BOARDS' is selected, check if ALL boards are risk management
  const allBoardsRiskManagement = selectedBoardId === 'ALL_BOARDS'
    ? boards.length > 0 && boards.every(board => {
        const templateType = board.templateType || board.template || '';
        return templateType === 'Risk Management';
      })
    : (selectedBoard?.templateType || selectedBoard?.template || '') === 'Risk Management';
  
  // Check if selected board is CRM template (with fallback to column matching)
  const isCRMTemplate = selectedBoardId === 'ALL_BOARDS'
    ? boards.length > 0 && boards.every(board => checkIfCRMTemplate(board))
    : checkIfCRMTemplate(selectedBoard) || checkIfCRMTemplate(selectedBoardDetails);
  const { projectId } = useParams();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [totalCards, setTotalCards] = useState(0);
  const [cardsPerColumn, setCardsPerColumn] = useState({});
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projectName, setProjectName] = useState('');
  const [projectsAddedLastTwoDays, setProjectsAddedLastTwoDays] = useState(0);
  const [assignedGroups, setAssignedGroups] = useState(['']);
  // const [boardName, setboardName] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [email, setEmail] = useState('');
  const [userId, setUserId] = useState();

  const getBoardName = (boardId) => {
    const selectedBoard = boards.find(board => board.id === boardId);
    return selectedBoard ? selectedBoard.boardName : 'All Boards';
  };

  useEffect(() => {
    const getUserDetails = async () => {
      const userData = getCookie('userData');
      const token = userData?.token;

      if (!token) {
        console.error('Token not found in cookies');
        return;
      }
      
      // Skip if we already have the data
      if (email && tenantId) {
        console.log('User details already loaded, skipping fetch');
        return;
      }
      
      try {
        const data = await fetchUserDetails(token);
        setEmail(data.email);
        setTenantId(data.tenantId);
      } catch (error) {
        console.error('Error fetching user details:', error);
      }
    };

    getUserDetails();
  }, [email, tenantId]); // Add dependencies to prevent unnecessary calls

  //   const getAssignedGroups = useCallback(async () => {
  //     const { userId, assignedGroups } = await fetchAssignedGroupsByEmail(email);
  //     setUserId(userId);
  //     setAssignedGroups(assignedGroups);
  // }, [email]);

  // useEffect(() => {
  //   getAssignedGroups();
  // }, [email]);

  useEffect(() => {
      const getGroups = async () => {
        if (!email) return; // Ensure userEmail is available
        
        // Skip if we already have the data
        if (assignedGroups.length > 0 && userId) {
          console.log('Assigned groups already loaded, skipping fetch');
          return;
        }
    
        try {
          const { userId, assignedGroups } = await fetchAssignedGroupsByEmail(email);
          setUserId(userId);
          setAssignedGroups(assignedGroups);
        } catch (error) {
          setError("Error fetching assigned groups: " + error.message);
        }
      };
    
      getGroups();
    }, [email, assignedGroups.length, userId]); // Add dependencies to prevent unnecessary calls

  // Fetch project name based on projectId
  useEffect(() => {
    const fetchProjectName = async () => {
      try {
        // Skip if we already have the project name
        if (projectName) {
          console.log('Project name already loaded, skipping fetch');
          return;
        }
        
        const projectData = await fetchProjectDetails(projectId);
        if (projectData) {
          setProjectName(projectData.prjName);
        } 
      } catch (error) {
        setError("Error fetching project name: " + error.message);
      }
    };
    fetchProjectName();
  }, [projectId, projectName]); // Add projectName dependency to prevent unnecessary calls

  useEffect(() => {
    const fetchCardData = async () => {
      if (!selectedBoardId || !tenantId) return;
      setLoading(true);
      try {
        // const cardsQuery = query(collection(db, 'cards'), where('projectId', '==', projectId));
        // const cardsSnapshot = await getDocs(cardsQuery);
        // const cardsList = cardsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        const cardsList = await fetchCardsForBoard(selectedBoardId, tenantId);
        // const cardsList = await fetchTasksByBoard(projectId, selectedBoardId);

        const fetchedCardsPerColumn = cardsList.reduce((acc, card) => {
          const columnName = card.cardStatus;
          acc[columnName] = (acc[columnName] || 0) + 1;
          return acc;
        }, {});
        
        setCardsPerColumn(fetchedCardsPerColumn);
        setTotalCards(cardsList.length);

      } catch (error) {
        setError("Error fetching data: " + error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchCardData();
  }, [selectedBoardId, tenantId]);

   useEffect(() => {
      const getBoardsData = async () => {
        if (!projectId || !assignedGroups || !tenantId) return;
        
        // Skip if we already have boards for this project
        if (boards.length > 0 && boards[0]?.projectId === projectId) {
          console.log('Boards already loaded for this project, skipping fetch');
          return;
        }
    
        try {
          setLoading(true);
          const fetchedBoards = await fetchBoards(projectId, assignedGroups, tenantId);
    
          setBoards(fetchedBoards);
    
          if (fetchedBoards.length > 0) {
            setSelectedBoardId(fetchedBoards[0].id);
          }
        } catch (error) {
          setError("Error fetching boards: " + error.message);
        } finally {
          setLoading(false);
        }
      };

      getBoardsData();
  }, [projectId, assignedGroups, tenantId, boards.length]); // Add boards.length to prevent unnecessary calls

  // Fetch selected board details to check template type via columns
  useEffect(() => {
    const loadBoardDetails = async () => {
      if (!selectedBoardId || selectedBoardId === 'ALL_BOARDS') {
        setSelectedBoardDetails(null);
        return;
      }
      
      try {
        const boardData = await fetchBoardDetails(selectedBoardId);
        setSelectedBoardDetails(boardData);
      } catch (error) {
        console.error('Error fetching board details:', error);
        setSelectedBoardDetails(null);
      }
    };

    loadBoardDetails();
  }, [selectedBoardId]);

//  useEffect(() => {
//     const fetchAnalytics = async () => {
//       if (selectedBoardId) {
//         try {
//           let cardsQuery;

//           if (selectedBoardId === 'ALL_BOARDS') {
//             cardsQuery = query(collection(db, 'cards'), where('projectId', '==', projectId));
//           } else {
//             cardsQuery = query(collection(db, 'cards'), where('boardId', '==', selectedBoardId));
//           }

//           const cardsSnapshot = await getDocs(cardsQuery);
//           const cards = cardsSnapshot.docs.map(doc => doc.data());

//           if (cards.length === 0) {
//             setAnalyticsData(null);
//             return;
//           }

//           const totalCardsInBoard = cards.length;
//           const cardsPerColumnInBoard = cards.reduce((acc, card) => {
//             const columnName = card.cardStatus;
//             acc[columnName] = (acc[columnName] || 0) + 1;
//             return acc;
//           }, {});

//           const twoDaysAgo = Timestamp.fromDate(new Date(Date.now() - 48 * 60 * 60 * 1000));

//           const addedProjectsQuery = query(collection(db, 'projects'), where('cardCreationDate', '>=', twoDaysAgo));
//           const addedProjectsSnapshot = await getDocs(addedProjectsQuery);
//           const addedProjects = addedProjectsSnapshot.docs.length;

//           const updatedProjectsQuery = query(collection(db, 'projects'), where('updatedAt', '>=', twoDaysAgo));
//           const updatedProjectsSnapshot = await getDocs(updatedProjectsQuery);
//           const updatedProjects = updatedProjectsSnapshot.docs.length;

//           setAnalyticsData({
//             totalCardsInBoard,
//             cardsPerColumnInBoard,
//             addedProjects,
//             updatedProjects,
//           });
//         } catch (error) {
//           setError("Error fetching analytics data: " + error.message);
//         }
//       }
//     };

//     fetchAnalytics();
//   }, [selectedBoardId, projectId]);

useEffect(() => {
  const fetchAnalytics = async () => {
    if (selectedBoardId) {
      try {
        const data = await fetchAnalyticsData(selectedBoardId, projectId);
        setAnalyticsData(data);
      } catch (error) {
        setError(error.message);
      }
    }
  };

  fetchAnalytics();
}, [selectedBoardId, projectId]); // Keep these dependencies as analytics should refresh when board changes

  const convertStringToDate = (dateString) => {
    // Check if dateString is null, undefined, or empty
    if (!dateString || typeof dateString !== 'string') {
      console.warn('Invalid dateString provided to convertStringToDate:', dateString);
      return new Date(0); // Return epoch date for invalid dates
    }
    
    const [day, month, year] = dateString.split('-');
    const monthMap = {
      Jan: 0,
      Feb: 1,
      Mar: 2,
      Apr: 3,
      May: 4,
      Jun: 5,
      Jul: 6,
      Aug: 7,
      Sep: 8,
      Oct: 9,
      Nov: 10,
      Dec: 11,
    };
    return new Date(year, monthMap[month], day);

  };

  // Calculate two days ago
  const twoDaysAgo = new Date();
  twoDaysAgo.setDate(twoDaysAgo.getDate() - 2); // Subtract 2 days


  // useEffect(() => {
  //   const fetchRecentCards = async () => {
  //     if (selectedBoardId || selectedBoardId === 'ALL_BOARDS') {
  //       try {
  //         let recentCardsQuery;

  //         // Check if the selectedBoardId is valid
  //         if (selectedBoardId === 'ALL_BOARDS') {
  //           recentCardsQuery = query(
  //             collection(db, 'cards'),
  //             where('projectId', '==', projectId)
  //           );
  //         } else {
  //           recentCardsQuery = query(
  //             collection(db, 'cards'),
  //             where('boardId', '==', selectedBoardId)
  //           );
  //         }

  //         const recentCardsSnapshot = await getDocs(recentCardsQuery);
          
  //         // Filter cards based on creation date
  //         const recentCards = recentCardsSnapshot.docs.filter(doc => {
  //           const cardData = doc.data();
  //           const cardDate = convertStringToDate(cardData.cardCreationDate); // Convert to Date object
  //           return cardDate >= twoDaysAgo; // Compare dates
  //         });

  //         console.log('Filtered recent cards count:', recentCards.length);
  //         setProjectsAddedLastTwoDays(recentCards.length);
  //       } catch (error) {
  //         setError("Error fetching recent cards: " + error.message);
  //         console.error(error);
  //       }
  //     }
  //   };

  //   fetchRecentCards();
  // }, [selectedBoardId, projectId]);

  useEffect(() => {
    const fetchRecentCards = async () => {
      if (selectedBoardId) {
        try {
          const count = await fetchRecentCardsFromDB(selectedBoardId, projectId, twoDaysAgo, convertStringToDate);
      
          setProjectsAddedLastTwoDays(count);
        } catch (error) {
          setError(error.message);
          console.error(error);
        }
      }
    };
  
    fetchRecentCards();
  }, [selectedBoardId, projectId]); // Keep these dependencies as recent cards should refresh when board changes
  

  const pieData = Object.entries(analyticsData?.cardsPerColumnInBoard || {}).map(([column, count]) => ({
    name: column,
    value: count
  }));

  const getPieChartOptions = () => ({
    title: {
      text: 'Total Cards Breakdown',
      left: 'center',
    },
    tooltip: {
      trigger: 'item'
    },
    legend: {
      orient: 'vertical',
      left: 'left',
    },
    series: [{
      name: 'Cards',
      type: 'pie',
      radius: '50%',
      data: pieData,
      emphasis: {
        itemStyle: {
          shadowBlur: 10,
          shadowOffsetX: 0,
          shadowColor: 'rgba(0, 0, 0, 0.5)'
        }
      }
    }]
  });


return (
  <Box sx={{ display: 'flex', flexDirection: 'row' }}>
    <Navbar />
    <SideDrawer
      projectId={projectId}
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      boards={boards}
    />
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        p: 3,
        backgroundColor: '#f5f5f5',
        minHeight: '100vh',
        mt: 10,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      {/* Dashboard Section */}
      <Box>
        {/* Header with Board Selector */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: 'white',
            mb: 4,
            borderRadius: 2,
            p: 2,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: '#1976d2',
              fontWeight: 'bold',
              textAlign: 'center',
            }}
          >
            {`${projectName.length > 15 ? projectName.substring(0, 15) + '...' : projectName}`} : 
            {`${getBoardName(selectedBoardId).length > 15 ? getBoardName(selectedBoardId).substring(0, 15) + '...' : getBoardName(selectedBoardId)}`}
          </Typography>

          {/* <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="board-select-label">Select Board</InputLabel>
            <Select
              labelId="board-select-label"
              value={selectedBoardId}
              onChange={(e) => setSelectedBoardId(e.target.value)}
            >
              <MenuItem value="ALL_BOARDS">All Boards</MenuItem>
              {boards.map((board) => (
                <MenuItem key={board.id} value={board.id}>
                  {board.boardName.length > 15 ? board.boardName.substring(0, 15) + '...' : board.boardName}
                </MenuItem>
              ))}
            </Select>
          </FormControl> */}

          {loading ? (
            <CircularProgress />
            ) : error ? (
            <Alert severity="error">{error}</Alert>
            ) : (
            <BoardDropdown
              boards={boards}
              selectedBoardId={selectedBoardId}
              setSelectedBoardId={setSelectedBoardId}
            />
            )}
        </Box>

        {/* Analytics Section */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={4} md={4}>
            <Paper elevation={6} sx={paperStyles('#00b4db', '#0083b0')}>
              <Typography variant="subtitle2" sx={titleStyles}>
                TOTAL CARDS IN BOARD
              </Typography>
              <Typography variant="h3" sx={numberStyles}>
                {analyticsData?.totalCardsInBoard ?? 0}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4} md={4}>
            <Paper elevation={6} sx={paperStyles('#ff6e7f', '#bfe9ff')}>
              <Typography variant="subtitle2" sx={titleStyles}>
                CARDS COMPLETED
              </Typography>
              <Typography variant="h3" sx={numberStyles}>
                {isCRMTemplate 
                  ? (analyticsData?.cardsPerColumnInBoard?.['Done'] || 0) + 
                    (analyticsData?.cardsPerColumnInBoard?.['Closed'] || 0) + 
                    (analyticsData?.cardsPerColumnInBoard?.['Won'] || 0)
                  : (analyticsData?.cardsPerColumnInBoard?.['Done'] || 0) + 
                    (analyticsData?.cardsPerColumnInBoard?.['Closed'] || 0)
                }
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4} md={4}>
            <Paper elevation={6} sx={paperStyles('#00b4db', '#0083b0')}>
              <Typography variant="subtitle2" sx={titleStyles}>
                CARDS ADDED IN LAST 2 DAYS
              </Typography>
              <Typography variant="h3" sx={numberStyles}>
                {projectsAddedLastTwoDays}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Metrics Charts Section */}
        {isCRMTemplate ? (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
              CRM Dashboard Metrics
            </Typography>
            <CRMMetricsCharts boardId={selectedBoardId} projectId={projectId} />
          </Box>
        ) : (
          (!allBoardsRiskManagement) && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
                Performance Metrics
              </Typography>
              <MetricsCharts boardId={selectedBoardId} projectId={projectId} />
            </Box>
          )
        )}

        {/* Pie Chart always visible */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card
              sx={{
                padding: 3,
                borderRadius: 3,
                boxShadow: 3,
                backgroundColor: '#fff',
              }}
            >
              <Typography
                variant="h5"
                component="div"
                sx={{
                  marginBottom: 2,
                  fontWeight: 600,
                  textAlign: 'center',
                  color: '#333',
                }}
              >
                NUMBER OF CARDS IN EACH COLUMN
              </Typography>
              <ReactECharts
                option={getPieChartOptions()}
                style={{ height: '400px', width: '100%' }}
              />
            </Card>
          </Grid>
        </Grid>
      </Box>


      {/* {(!allBoardsRiskManagement) && (
        <Box sx={{ mt: 4 }}>
          <MilestoneManager projectId={projectId} />
        </Box>
      )} */}
    </Box>
  </Box>
);



};

const paperStyles = (color1, color2) => ({
  padding: 2,
  background:` linear-gradient(135deg, ${color1} 0%, ${color2} 100%)`,
  borderRadius: 2,
  color: '#fff'
});

const titleStyles = {
  fontWeight: 'bold',
  marginBottom: 1
};

const numberStyles = {
  fontWeight: 'bold'
};

export default DashboardPage;